import React, {useEffect, useState} from 'react';
import {NavigationContainer} from '@react-navigation/native';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import {createStackNavigator} from '@react-navigation/stack';
import {createDrawerNavigator} from '@react-navigation/drawer';
import {ActivityIndicator, View, StyleSheet, Platform, Easing} from 'react-native';

import {AuthProvider, useAuth} from './src/contexts/AuthContext';
import {PlayerProvider, usePlayer} from './src/contexts/PlayerContext';
import {ThemeProvider} from './src/contexts/ThemeContext';
import LoginScreen from './src/screens/LoginScreen';
import ListScreen from './src/screens/ListScreen';
import PlayScreen from './src/screens/PlayScreen';
import GameMenuScreen from './src/components/GameMenuScreen';
import CreditsScreen from './src/screens/CreditsScreen';
import HallOfFameScreen from './src/screens/HallOfFameScreen';
import SettingsScreen from './src/screens/SettingsScreen';
import GameSettingsScreen from './src/screens/GameSettingsScreen';
import AppHeader from './src/components/AppHeader';
import PlayDrawer from './src/components/PlayDrawer';

import ConfigService from './src/services/configService';
import apiService from './src/services/apiService';
import databaseService from './src/services/databaseService';
import syncService from './src/services/syncService';
import sessionBridgeService from './src/services/sessionBridgeService';
import GameRegistry from './src/systems/GameRegistry';
import {SimpleSystem} from './src/systems/simple/SimpleSystem';
import {D6System} from './src/systems/d6/D6System';
import {EscapeSystem} from './src/systems/escape/EscapeSystem';

// Utiliser Stack Navigator au lieu de NativeStack pour avoir des transitions personnalisées
const Stack = createStackNavigator();
const Drawer = createDrawerNavigator();

// Configuration des transitions personnalisées
const transitionConfig = {
  transitionSpec: {
    open: {
      animation: 'timing',
      config: {
        duration: 400,
        easing: Easing.out(Easing.poly(4)),
      },
    },
    close: {
      animation: 'timing',
      config: {
        duration: 350,
        easing: Easing.in(Easing.poly(4)),
      },
    },
  },
  cardStyleInterpolator: ({current, next, layouts}: any) => {
    return {
      cardStyle: {
        transform: [
          {
            translateX: current.progress.interpolate({
              inputRange: [0, 1],
              outputRange: [layouts.screen.width, 0],
            }),
          },
        ],
        opacity: current.progress.interpolate({
          inputRange: [0, 0.5, 0.9, 1],
          outputRange: [0, 0.25, 0.7, 1],
        }),
      },
      overlayStyle: {
        opacity: current.progress.interpolate({
          inputRange: [0, 1],
          outputRange: [0, 0.5],
        }),
      },
    };
  },
};

// Transition fade pour les écrans modaux (Credits, HallOfFame)
const modalTransitionConfig = {
  transitionSpec: {
    open: {
      animation: 'timing',
      config: {
        duration: 300,
        easing: Easing.out(Easing.cubic),
      },
    },
    close: {
      animation: 'timing',
      config: {
        duration: 250,
        easing: Easing.in(Easing.cubic),
      },
    },
  },
  cardStyleInterpolator: ({current}: any) => {
    return {
      cardStyle: {
        opacity: current.progress.interpolate({
          inputRange: [0, 1],
          outputRange: [0, 1],
        }),
        transform: [
          {
            scale: current.progress.interpolate({
              inputRange: [0, 1],
              outputRange: [0.9, 1],
            }),
          },
        ],
      },
      overlayStyle: {
        opacity: current.progress.interpolate({
          inputRange: [0, 1],
          outputRange: [0, 0.5],
        }),
      },
    };
  },
};

// Transition "porte de garage" depuis le haut pour GameMenu
const garageDoorTransitionConfig = {
  transitionSpec: {
    open: {
      animation: 'timing',
      config: {
        duration: 0, // Désactiver la transition de navigation, on gère tout avec l'animation interne
        easing: Easing.out(Easing.cubic),
      },
    },
    close: {
      animation: 'timing',
      config: {
        duration: 0, // Désactiver la transition de navigation, on gère tout avec l'animation interne
        easing: Easing.in(Easing.cubic),
      },
    },
  },
  cardStyleInterpolator: ({current, layouts}: any) => {
    return {
      cardStyle: {
        // Pas de transformation ici, on laisse l'animation interne du composant gérer
        opacity: 1, // Toujours visible pour que l'animation interne fonctionne
        backgroundColor: 'transparent', // Fond transparent pour voir la liste en arrière-plan
      },
      overlayStyle: {
        // Pas d'overlay, on veut voir la liste des aventures en arrière-plan
        // Toujours transparent, même au début de la transition
        opacity: current.progress.interpolate({
          inputRange: [0, 1],
          outputRange: [0, 0], // Toujours 0, jamais d'overlay
        }),
      },
    };
  },
};

// DrawerNavigator spécifique pour PlayScreen avec PlayDrawer
const PlayDrawerNavigator: React.FC = () => {
  return (
    <Drawer.Navigator
      drawerContent={(props) => <PlayDrawer {...props} />}
      screenOptions={{
        headerShown: false,
        drawerType: 'slide',
        drawerStyle: {
          width: 320,
        },
        overlayColor: 'rgba(0,0,0,0.5)',
      }}>
      <Drawer.Screen name="PlayContent" component={PlayScreen} />
    </Drawer.Navigator>
  );
};

const AppNavigator: React.FC = () => {
  const {isLoggedIn, loading} = useAuth();
  const {initTestGame} = usePlayer();
  const [initialRouteName, setInitialRouteName] = useState<string | null>(null);
  const [hasHandledTestRoute, setHasHandledTestRoute] = useState(false);
  const [isInitializingTestGame, setIsInitializingTestGame] = useState(false);

  // Gérer le routing web de test : /#/test/:adventureURI/step/:stepNumber
  useEffect(() => {
    if (loading) {
      return;
    }

    // Détecter le mode test web via le hash
    let isWebTestRoute = false;
    let adventureURIFromHash: string | null = null;
    let stepNumberFromHash: number | null = null;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const rawHash = window.location.hash || '';
      const hashPath = rawHash.startsWith('#') ? rawHash.substring(1) : rawHash;

      if (hashPath.startsWith('/test/')) {
        // Parser le hash pour extraire le path et les query parameters
        const hashParts = hashPath.split('?');
        const pathPart = hashParts[0];
        const queryPart = hashParts[1] || '';
        
        const parts = pathPart.split('/');
        if (parts.length >= 5) {
          const adventureURI = parts[2];
          const stepNumber = parseInt(parts[4], 10);
          if (adventureURI && !isNaN(stepNumber)) {
            isWebTestRoute = true;
            adventureURIFromHash = adventureURI;
            stepNumberFromHash = stepNumber;
            
            // Définir le titre de la fenêtre depuis l'URL (query parameter dans le hash)
            if (queryPart) {
              const urlParams = new URLSearchParams(queryPart);
              const title = urlParams.get('title');
              if (title) {
                document.title = decodeURIComponent(title);
                console.log(`[AppNavigator] Window title set to: ${document.title}`);
              } else {
                // Fallback : utiliser le titre par défaut si pas de titre dans l'URL
                document.title = document.title || 'L\'Aventurier';
              }
            } else {
              // Fallback : utiliser le titre par défaut si pas de query parameters
              document.title = document.title || 'L\'Aventurier';
            }
          }
        }
      }
    }

    // Cas spécial : route de test web en popup -> le token vient du parent via le bridge, pas besoin de login Supabase
    // Doit être traité AVANT le check isLoggedIn pour ne pas rediriger vers Login
    if (isWebTestRoute && adventureURIFromHash && stepNumberFromHash != null) {
      console.log(
        `[AppNavigator] Web test route detected: adventureURI=${adventureURIFromHash}, stepNumber=${stepNumberFromHash}`,
      );

      if (!hasHandledTestRoute) {
        setHasHandledTestRoute(true);
        setIsInitializingTestGame(true);
        // Initialiser le jeu de test en mémoire (API éditeur)
        // Attendre que l'initialisation se termine avant de naviguer
        initTestGame(adventureURIFromHash, stepNumberFromHash)
          .then(() => {
            console.log('[AppNavigator] Test game initialized successfully, navigating to Play');
            setInitialRouteName('Play');
            setIsInitializingTestGame(false);
          })
          .catch((error) => {
            console.error('[AppNavigator] Failed to initialize test game from URL:', error);
            // En cas d'erreur, fallback sur Main
            setInitialRouteName('Main');
            setIsInitializingTestGame(false);
          });
        // Ne pas définir initialRouteName ici, attendre que initTestGame se termine
        return;
      }

      // Si on a déjà traité la route et que l'initialisation est terminée, naviguer vers Play
      if (!isInitializingTestGame && !initialRouteName) {
        setInitialRouteName('Play');
      }
      return;
    }

    // Si l'utilisateur n'est pas connecté, afficher l'écran de login (hors mode test popup)
    if (!isLoggedIn) {
      setInitialRouteName('Login');
      return;
    }

    // Fallback normal : route principale de la liste (mode published)
    if (!initialRouteName) {
      setInitialRouteName('Main');
    }
  }, [isLoggedIn, loading, initTestGame, hasHandledTestRoute, initialRouteName]);

  if (loading || !initialRouteName || isInitializingTestGame) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  return (
    <Stack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={{
        headerShown: false,
        gestureEnabled: true,
        gestureDirection: 'horizontal',
        cardStyle: {backgroundColor: 'transparent'},
        cardOverlayEnabled: false, // Désactiver complètement l'overlay pour tous les écrans
        ...transitionConfig,
      }}>
      {!isLoggedIn ? (
        <Stack.Screen
          name="Login"
          component={LoginScreen}
          options={{
            ...modalTransitionConfig,
          }}
        />
      ) : (
        <>
          <Stack.Screen
            name="Main"
            component={ListScreen}
            options={{
              header: () => <AppHeader />,
              ...transitionConfig,
            }}
          />
          <Stack.Screen
            name="Settings"
            component={SettingsScreen}
            options={{
              ...modalTransitionConfig,
              presentation: 'modal',
            }}
          />
          <Stack.Screen
            name="GameSettings"
            component={GameSettingsScreen}
            options={{
              ...transitionConfig,
            }}
          />
          <Stack.Screen
            name="GameMenu"
            component={GameMenuScreen}
            options={{
              cardStyle: {backgroundColor: 'transparent', opacity: 1}, // Transparent et visible dès le début
              cardOverlayEnabled: false, // Désactiver complètement l'overlay
              animationEnabled: false, // Désactiver l'animation de navigation car on gère tout avec l'animation interne
              gestureEnabled: true,
              gestureDirection: 'vertical',
              gestureResponseDistance: {
                vertical: 200,
              },
              // Empêcher React Navigation d'afficher un fond par défaut
              presentation: 'transparentModal',
            }}
          />
          <Stack.Screen
            name="Play"
            component={PlayDrawerNavigator}
            options={{
              ...transitionConfig,
            }}
          />
          <Stack.Screen
            name="Credits"
            component={CreditsScreen}
            options={{
              ...modalTransitionConfig,
              presentation: 'modal',
            }}
          />
          <Stack.Screen
            name="HallOfFame"
            component={HallOfFameScreen}
            options={{
              ...modalTransitionConfig,
              presentation: 'modal',
            }}
          />
        </>
      )}
    </Stack.Navigator>
  );
};

const App: React.FC = () => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        // Enregistrer les systèmes de jeu
        const simpleSystem = new SimpleSystem();
        const d6System = new D6System();
        const escapeSystem = new EscapeSystem();

        // Enregistrer SIMPLE comme système par défaut (pour les aventures sans type spécifique)
        GameRegistry.registerSystem('SIMPLE', simpleSystem);
        GameRegistry.registerSystem('D6', d6System);
        GameRegistry.registerSystem('ESCAPE', escapeSystem);
        console.log('[App] Registered game systems: SIMPLE, D6, ESCAPE');

        // Enregistrer les conditions de base après l'enregistrement des systèmes
        simpleSystem.registerConditionTypes();
        d6System.registerConditionTypes(); // Enregistrer les conditions D6
        
        // Enregistrer les step builders
        simpleSystem.registerStepBuilders();

        // Initialiser les services
        await ConfigService.initialize();
        await databaseService.init();
        apiService.init();
        syncService.init(); // Initialiser la synchronisation en arrière-plan
        
        // Initialiser le bridge de session pour recevoir les tokens depuis laventurier-ui (parent)
        // En mode popup/test, le token vient du parent via postMessage.
        // L'origine parente peut venir de l'URL (?parent_origin=...) quand on ouvre depuis l'éditeur, sinon env ou défaut localhost.
        let parentOrigin = process.env.REACT_APP_PARENT_ORIGIN || 'http://localhost:8081';
        if (typeof window !== 'undefined' && window.location.hash) {
          const qIndex = window.location.hash.indexOf('?');
          if (qIndex >= 0) {
            const params = new URLSearchParams(window.location.hash.substring(qIndex));
            const fromUrl = params.get('parent_origin');
            if (fromUrl) parentOrigin = fromUrl;
          }
        }
        sessionBridgeService.init(parentOrigin);
        
        setInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
      }
    };

    init();
  }, []);

  if (!initialized) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  return (
    <NavigationContainer>
      <ThemeProvider>
        <AuthProvider>
          <PlayerProvider>
            <AppNavigator />
          </PlayerProvider>
        </AuthProvider>
      </ThemeProvider>
    </NavigationContainer>
  );
};

const styles = StyleSheet.create({
  centerContainer: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? '100vh' : undefined,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
});

export default App;
